<template>
  <div>
    <b-modal
      ref="importModal"
      :title="$t('USERS.IMPORT.TITLE')"
      size="xl"
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      @hidden="resetImport"
    >
      <b-container fluid>
        <div class="import-steps mb-4">
          <span :class="stepClass('upload')"
            >1. {{ $t("USERS.IMPORT.STEP_UPLOAD") }}</span
          >
          <span :class="stepClass('validate')"
            >2. {{ $t("USERS.IMPORT.STEP_VALIDATE") }}</span
          >
          <span :class="stepClass('process')"
            >3. {{ $t("USERS.IMPORT.STEP_PROCESS") }}</span
          >
          <span :class="stepClass('result')"
            >4. {{ $t("USERS.IMPORT.STEP_RESULTS") }}</span
          >
        </div>

        <template v-if="stage === 'upload' || stage === 'validating'">
          <b-alert show variant="secondary">
            {{ $t("USERS.IMPORT.INTRO") }}
            <b-button variant="link" class="p-0 ml-1" @click="openFormatHelp">
              {{ $t("USERS.IMPORT.FORMAT_HELP") }}
            </b-button>
          </b-alert>

          <b-form-group :label="$t('USERS.IMPORT.FILE_LABEL')">
            <b-form-file
              v-model="importFile"
              accept=".csv,text/csv"
              :disabled="stage === 'validating'"
              :placeholder="$t('USERS.IMPORT.FILE_PLACEHOLDER')"
              :drop-placeholder="$t('USERS.IMPORT.FILE_DROP_PLACEHOLDER')"
              @input="onFileSelected"
            ></b-form-file>
          </b-form-group>

          <small class="text-muted">{{ $t("USERS.IMPORT.FILE_LIMIT") }}</small>

          <div v-if="stage === 'validating'" class="text-center py-5">
            <b-spinner variant="primary" class="mr-2"></b-spinner>
            {{ $t("USERS.IMPORT.VALIDATING") }}
          </div>
        </template>

        <template v-else-if="stage === 'errors'">
          <b-alert show variant="danger">
            {{
              $t("USERS.IMPORT.VALIDATION_FAILED", {
                count: validationErrors.length,
              })
            }}
          </b-alert>
          <div class="import-table-scroll">
            <b-table
              small
              striped
              responsive
              :items="validationErrors"
              :fields="validationErrorFields"
            ></b-table>
          </div>
          <p class="text-muted mt-3 mb-0">
            {{ $t("USERS.IMPORT.CORRECT_AND_REUPLOAD") }}
          </p>
        </template>

        <template v-else-if="stage === 'ready'">
          <b-alert show variant="success">{{
            $t("USERS.IMPORT.VALIDATION_SUCCESS")
          }}</b-alert>

          <h6>{{ $t("USERS.IMPORT.RESOURCE_SUMMARY") }}</h6>
          <b-table
            small
            bordered
            responsive
            :items="resourceRows"
            :fields="resourceFields"
          ></b-table>

          <h6 class="mt-4">
            {{ $t("USERS.IMPORT.PREVIEW", { count: rows.length }) }}
          </h6>
          <div class="import-table-scroll">
            <b-table
              small
              striped
              responsive
              :items="rows"
              :fields="previewFields"
            >
              <template v-slot:cell(address)="data">
                {{ data.item.username }}@{{ data.item.domain }}
              </template>
              <template v-slot:cell(accountLabel)="data">
                {{ accountTypeLabel(data.item.accountType) }}
              </template>
              <template v-slot:cell(quotaDisplay)="data">
                {{ quotaDisplay(data.item) }}
              </template>
            </b-table>
          </div>
        </template>

        <template v-else-if="stage === 'importing'">
          <b-progress
            :value="processedCount"
            :max="rows.length"
            height="1.5rem"
            show-progress
            animated
            class="mb-3"
          ></b-progress>
          <p class="mb-2">
            {{
              $t("USERS.IMPORT.PROGRESS", {
                current: processedCount,
                total: rows.length,
              })
            }}
          </p>
          <p v-if="nextRowDelay > 0" class="text-muted">
            {{ $t("USERS.IMPORT.NEXT_IN", { seconds: nextRowDelay }) }}
          </p>

          <div class="import-table-scroll">
            <b-table
              small
              striped
              responsive
              :items="rows"
              :fields="resultFields"
            >
              <template v-slot:cell(address)="data">
                {{ data.item.username }}@{{ data.item.domain }}
              </template>
              <template v-slot:cell(accountLabel)="data">
                {{ accountTypeLabel(data.item.accountType) }}
              </template>
              <template v-slot:cell(status)="data">
                <b-badge :variant="statusVariant(data.item.status)">
                  {{ statusLabel(data.item.status) }}
                </b-badge>
              </template>
            </b-table>
          </div>
        </template>

        <template v-else-if="stage === 'complete'">
          <b-progress
            :value="rows.length"
            :max="rows.length"
            height="1.5rem"
            variant="success"
            class="mb-3"
          ></b-progress>

          <b-alert v-if="failedRows.length === 0" show variant="success">
            {{
              $t("USERS.IMPORT.IMPORT_SUCCESS", {
                count: successfulRows.length,
              })
            }}
          </b-alert>
          <b-alert v-else show variant="warning">
            {{
              $t("USERS.IMPORT.IMPORT_PARTIAL", {
                success: successfulRows.length,
                failed: failedRows.length,
              })
            }}
          </b-alert>

          <div v-if="failedRows.length" class="import-table-scroll">
            <b-table
              small
              striped
              responsive
              :items="failedRows"
              :fields="failureFields"
            >
              <template v-slot:cell(address)="data">
                {{ data.item.username }}@{{ data.item.domain }}
              </template>
            </b-table>
          </div>
        </template>
      </b-container>

      <template v-slot:modal-footer>
        <template v-if="stage === 'upload'">
          <b-button variant="outline-secondary" @click="closeImport">
            {{ $t("COMMON.CANCEL") }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="!importFile"
            @click="validateImport"
          >
            {{ $t("USERS.IMPORT.VALIDATE") }}
          </b-button>
        </template>

        <template v-else-if="stage === 'validating'">
          <b-button variant="primary" disabled>
            <b-spinner small class="mr-2"></b-spinner
            >{{ $t("USERS.IMPORT.VALIDATING") }}
          </b-button>
        </template>

        <template v-else-if="stage === 'errors'">
          <b-button variant="primary" @click="backToUpload">
            {{ $t("USERS.IMPORT.BACK_TO_UPLOAD") }}
          </b-button>
        </template>

        <template v-else-if="stage === 'ready'">
          <b-button variant="outline-secondary" @click="backToUpload">
            {{ $t("USERS.IMPORT.BACK_TO_UPLOAD") }}
          </b-button>
          <b-button variant="primary" @click="processImport">
            {{ $t("USERS.IMPORT.PROCESS_FILE") }}
          </b-button>
        </template>

        <template v-else-if="stage === 'importing'">
          <b-button variant="primary" disabled>{{
            $t("USERS.IMPORT.PROCESSING")
          }}</b-button>
        </template>

        <template v-else-if="stage === 'complete'">
          <b-button variant="primary" @click="closeImport">
            {{ $t("COMMON.CLOSE") }}
          </b-button>
        </template>
      </template>
    </b-modal>

    <b-modal
      ref="formatModal"
      :title="$t('USERS.IMPORT.FORMAT_TITLE')"
      size="lg"
      centered
      ok-only
      :ok-title="$t('COMMON.CLOSE')"
    >
      <b-form-group :label="$t('USERS.IMPORT.FORMAT_ACCOUNT_TYPE')">
        <b-form-select
          v-model="formatAccountType"
          :options="formatAccountTypeOptions"
        ></b-form-select>
      </b-form-group>

      <b-alert show variant="light" class="py-2">
        {{ selectedFormatDescription }}
      </b-alert>

      <b-row class="mb-3">
        <b-col :md="formatOptionalColumns.length ? 6 : 12">
          <h6>{{ $t("USERS.IMPORT.FORMAT_REQUIRED_COLUMNS") }}</h6>
          <div class="format-column-list">
            <code v-for="column in formatRequiredColumns" :key="column">
              {{ column }}
            </code>
          </div>
        </b-col>
        <b-col v-if="formatOptionalColumns.length" md="6">
          <h6>{{ $t("USERS.IMPORT.FORMAT_OPTIONAL_COLUMNS") }}</h6>
          <div class="format-column-list">
            <code v-for="column in formatOptionalColumns" :key="column">
              {{ column }}
            </code>
          </div>
        </b-col>
      </b-row>

      <h6>{{ $t("USERS.IMPORT.FORMAT_EXAMPLE") }}</h6>
      <pre class="import-format">{{ selectedFormatExample }}</pre>
      <p class="small text-muted mb-1">
        {{ $t("USERS.IMPORT.FORMAT_HEADER_OPTIONAL") }}
      </p>
      <p class="small text-muted mb-1">
        {{ $t("USERS.IMPORT.FORMAT_QUOTES") }}
      </p>
      <p class="small text-muted">
        {{ $t("USERS.IMPORT.FORMAT_MIXED") }}
      </p>
      <b-button variant="outline-primary" @click="downloadTemplate">
        <v-icon small class="mr-1">get_app</v-icon
        >{{ $t("USERS.IMPORT.DOWNLOAD_TEMPLATE") }}
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import { postRequest } from "@/api/request.js";
import JwtService from "@/common/jwt.service";
import { isBlankCsvRow, parseCsv } from "@/common/csv";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_IMPORT_ROWS = 1000;
const IMPORT_DELAY_SECONDS = 5;
const ALLOWED_ACCOUNT_TYPES = [1, 2, 3, 4, 9];
const REQUIRED_HEADERS = ["account_type", "username", "domain"];
const POSITIONAL_HEADER_MAP = {
  account_type: 0,
  username: 1,
  domain: 2,
  password: 3,
  quota_gb: 4,
  full_name: 5,
  account_subtype: 6,
};

const FORMAT_DEFINITIONS = {
  1: {
    required: ["account_type", "username", "domain", "password", "quota_gb"],
    optional: ["full_name"],
    example: '1,jane,example.com,"Strong,Pass1!",10,Jane Doe',
    description: "USERS.IMPORT.FORMAT_TYPE_1",
  },
  2: {
    required: ["account_type", "username", "domain", "password", "quota_gb"],
    optional: ["full_name"],
    example: "2,john,example.com,Strong.Pass2!,25,John Smith",
    description: "USERS.IMPORT.FORMAT_TYPE_2",
  },
  3: {
    required: ["account_type", "username", "domain", "password", "quota_gb"],
    optional: ["full_name", "account_subtype"],
    example: '3,alex,example.com,"Strong,Pass3!",20,Alex Smith,0',
    description: "USERS.IMPORT.FORMAT_TYPE_3",
  },
  4: {
    required: ["account_type", "username", "domain", "password", "quota_gb"],
    optional: ["full_name"],
    example: "4,auditor,audit.example.com,Strong.Pass4!,10,Audit User",
    description: "USERS.IMPORT.FORMAT_TYPE_4",
  },
  9: {
    required: ["account_type", "username", "domain"],
    optional: [],
    example: "9,external,example.com",
    description: "USERS.IMPORT.FORMAT_TYPE_9",
  },
};

const HEADER_ALIASES = {
  accounttype: "account_type",
  type: "account_type",
  username: "username",
  user: "username",
  domain: "domain",
  password: "password",
  pass: "password",
  quota: "quota_gb",
  quotagb: "quota_gb",
  quotaingb: "quota_gb",
  fullname: "full_name",
  name: "full_name",
  uname: "full_name",
  accountsubtype: "account_subtype",
  subtype: "account_subtype",
  oexsubtype: "account_subtype",
};

const USERNAME_REGEX = /^[0-9a-zA-Z\-_.+:]+$/;
const DOMAIN_REGEX = /^[a-z0-9+-]+(\.[a-z0-9+-]+)*(\.[a-z]{2,11})$/i;
const SIMPLE_PASSWORD_REGEX = new RegExp(
  "^[\\w_~\\/.,:;?=!|@#$%^&*()\\[\\]{}><+-]+$"
);
const COMPLEX_PASSWORD_REGEX =
  /^\S*(?=\S{8,})(?=\S*[a-z])(?=\S*[A-Z])(?=\S*[\W_])(?=\S*[\d])\S*$/;
const QUOTA_GB_REGEX = /^(?:0|[1-9][0-9]*)(?:\.[0-9]{1,3})?$/;
const FULL_NAME_REGEX = /^[\p{L}0-9 '._&@-]{0,64}$/u;
const CSV_ERROR_TRANSLATIONS = {
  unexpected_character_after_quote: "USERS.IMPORT.CSV_UNEXPECTED_AFTER_QUOTE",
  unescaped_quote: "USERS.IMPORT.CSV_UNESCAPED_QUOTE",
  unterminated_quote: "USERS.IMPORT.CSV_UNTERMINATED_QUOTE",
};

export default {
  name: "UserImportModal",
  props: {
    regularDomains: {
      type: Array,
      default: () => [],
    },
    auditDomains: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      stage: "upload",
      importFile: null,
      rows: [],
      fileErrors: [],
      stats: null,
      processedCount: 0,
      nextRowDelay: 0,
      delayTimer: null,
      formatAccountType: 1,
      defaults: {
        timezone: "",
        dateformat: "",
      },
    };
  },
  computed: {
    formatAccountTypeOptions() {
      return ALLOWED_ACCOUNT_TYPES.map((accountType) => ({
        value: accountType,
        text: `${accountType} — ${this.accountTypeLabel(accountType)}`,
      }));
    },
    selectedFormatDefinition() {
      return (
        FORMAT_DEFINITIONS[this.formatAccountType] || FORMAT_DEFINITIONS[1]
      );
    },
    formatRequiredColumns() {
      return this.selectedFormatDefinition.required;
    },
    formatOptionalColumns() {
      return this.selectedFormatDefinition.optional;
    },
    selectedFormatDescription() {
      return this.$t(this.selectedFormatDefinition.description);
    },
    selectedFormatExample() {
      const columns = this.formatRequiredColumns.concat(
        this.formatOptionalColumns
      );
      return `${columns.join(",")}\n${this.selectedFormatDefinition.example}`;
    },
    validationErrors() {
      const errors = this.fileErrors.slice();
      this.rows.forEach((row) => {
        row.errors.forEach((error) => {
          errors.push({ line: row.line, ...error });
        });
      });
      return errors;
    },
    validationErrorFields() {
      return [
        { key: "line", label: this.$t("USERS.IMPORT.LINE") },
        { key: "field", label: this.$t("USERS.IMPORT.FIELD") },
        { key: "message", label: this.$t("USERS.IMPORT.ERROR") },
      ];
    },
    resourceFields() {
      return [
        { key: "resource", label: this.$t("USERS.IMPORT.RESOURCE") },
        { key: "requested", label: this.$t("USERS.IMPORT.REQUESTED") },
        { key: "available", label: this.$t("USERS.IMPORT.AVAILABLE") },
        { key: "remaining", label: this.$t("USERS.IMPORT.REMAINING") },
      ];
    },
    previewFields() {
      return [
        { key: "line", label: this.$t("USERS.IMPORT.LINE") },
        { key: "address", label: this.$t("COMMON.ADDRESS") },
        { key: "accountLabel", label: this.$t("COMMON.TYPE") },
        { key: "quotaDisplay", label: this.$t("COMMON.QUOTA") },
        { key: "fullName", label: this.$t("COMMON.FULL_NAME") },
      ];
    },
    resultFields() {
      return [
        { key: "line", label: this.$t("USERS.IMPORT.LINE") },
        { key: "address", label: this.$t("COMMON.ADDRESS") },
        { key: "accountLabel", label: this.$t("COMMON.TYPE") },
        { key: "status", label: this.$t("COMMON.STATUS") },
        { key: "message", label: this.$t("USERS.IMPORT.SERVER_RESPONSE") },
      ];
    },
    failureFields() {
      return [
        { key: "line", label: this.$t("USERS.IMPORT.LINE") },
        { key: "address", label: this.$t("COMMON.ADDRESS") },
        { key: "message", label: this.$t("USERS.IMPORT.ERROR") },
      ];
    },
    requestedCounts() {
      return this.rows.reduce(
        (counts, row) => {
          if (row.accountType === 1 || row.accountType === 4) counts.basic += 1;
          if (row.accountType === 2) counts.enhanced += 1;
          if (row.accountType === 3) counts.oex += 1;
          if (row.accountType === 9) counts.external += 1;
          if ([1, 2, 3, 4].includes(row.accountType))
            counts.quota += row.quotaMb;
          return counts;
        },
        { basic: 0, enhanced: 0, oex: 0, external: 0, quota: 0 }
      );
    },
    resourceRows() {
      if (!this.stats) return [];

      const definitions = [
        {
          resource: this.$t("USERS.IMPORT.BASIC_AUDIT_RESOURCE"),
          requested: this.requestedCounts.basic,
          current: this.numberStat("curemails"),
          maximum: this.numberStat("maxemails"),
        },
        {
          resource: this.$t("USERS.ENHANCED"),
          requested: this.requestedCounts.enhanced,
          current: this.numberStat("cureemails"),
          maximum: this.numberStat("maxeemails"),
        },
        {
          resource: this.$t("USERS.OEX"),
          requested: this.requestedCounts.oex,
          current: this.numberStat("curoexemails"),
          maximum: this.numberStat("maxoexemails"),
        },
        {
          resource: this.$t("USERS.IMPORT.EXTERNAL"),
          requested: this.requestedCounts.external,
          current: this.numberStat("curocemails"),
          maximum: this.numberStat("maxocemails"),
        },
      ];

      const rows = definitions.map((item) => {
        const available = Math.max(0, item.maximum - item.current);
        return {
          resource: item.resource,
          requested: item.requested,
          available,
          remaining: Math.max(0, available - item.requested),
        };
      });

      const quotaAvailableMb = Math.max(
        0,
        this.numberStat("maxquota") - this.numberStat("curquota")
      );
      const quotaAutogrows = String(this.stats.autogrow_quota) === "1";
      rows.push({
        resource: this.$t("USERS.IMPORT.QUOTA_RESOURCE"),
        requested: this.formatGb(this.requestedCounts.quota),
        available: quotaAutogrows
          ? this.$t("USERS.IMPORT.AUTO_GROW")
          : this.formatGb(quotaAvailableMb),
        remaining: quotaAutogrows
          ? this.$t("USERS.IMPORT.AUTO_GROW")
          : this.formatGb(
              Math.max(0, quotaAvailableMb - this.requestedCounts.quota)
            ),
      });

      return rows;
    },
    successfulRows() {
      return this.rows.filter((row) => row.status === "success");
    },
    failedRows() {
      return this.rows.filter((row) => row.status === "failed");
    },
  },
  beforeDestroy() {
    this.clearDelayTimer();
    this.clearSensitiveRows();
  },
  methods: {
    open() {
      this.resetImport();
      this.$nextTick(() => this.$refs.importModal.show());
    },
    closeImport() {
      this.$refs.importModal.hide();
    },
    openFormatHelp() {
      this.$refs.formatModal.show();
    },
    resetImport() {
      this.clearDelayTimer();
      this.clearSensitiveRows();
      this.stage = "upload";
      this.importFile = null;
      this.rows = [];
      this.fileErrors = [];
      this.stats = null;
      this.processedCount = 0;
      this.nextRowDelay = 0;
      this.defaults = { timezone: "", dateformat: "" };
    },
    onFileSelected() {
      this.clearSensitiveRows();
      this.rows = [];
      this.fileErrors = [];
      this.stats = null;
      this.processedCount = 0;
      this.stage = "upload";
    },
    backToUpload() {
      this.resetImport();
    },
    clearSensitiveRows() {
      this.rows.forEach((row) => {
        row.password = "";
      });
    },
    clearDelayTimer() {
      if (this.delayTimer) clearInterval(this.delayTimer);
      this.delayTimer = null;
    },
    stepClass(step) {
      const order = { upload: 0, validate: 1, process: 2, result: 3 };
      const active = {
        upload: 0,
        validating: 1,
        errors: 1,
        ready: 1,
        importing: 2,
        complete: 3,
      }[this.stage];
      return [
        "import-step",
        { active: order[step] === active, complete: order[step] < active },
      ];
    },
    normalizeHeader(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "");
    },
    readFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () =>
          reject(reader.error || new Error("Unable to read file."));
        reader.readAsText(file, "UTF-8");
      });
    },
    addFileError(message, line = "", field = "") {
      this.fileErrors.push({ line, field, message });
    },
    addRowError(row, field, message) {
      row.errors.push({ field, message });
    },
    buildHeaderMap(headerRow) {
      const map = {};
      headerRow.values.forEach((value, index) => {
        const normalized = this.normalizeHeader(value);
        const field = HEADER_ALIASES[normalized];
        if (!field) {
          this.addFileError(
            this.$t("USERS.IMPORT.UNKNOWN_COLUMN", {
              column: value || index + 1,
            }),
            headerRow.line,
            value
          );
          return;
        }
        if (Object.prototype.hasOwnProperty.call(map, field)) {
          this.addFileError(
            this.$t("USERS.IMPORT.DUPLICATE_COLUMN", { column: value }),
            headerRow.line,
            value
          );
          return;
        }
        map[field] = index;
      });

      REQUIRED_HEADERS.forEach((field) => {
        if (!Object.prototype.hasOwnProperty.call(map, field)) {
          this.addFileError(
            this.$t("USERS.IMPORT.MISSING_COLUMN", { column: field }),
            headerRow.line,
            field
          );
        }
      });
      return map;
    },
    hasHeaderRow(csvRow) {
      const firstValue = String(csvRow.values[0] || "").trim();
      if (/^[0-9]+$/.test(firstValue)) return false;
      return csvRow.values.some((value) => {
        const normalized = this.normalizeHeader(value);
        return Boolean(HEADER_ALIASES[normalized]);
      });
    },
    fieldValue(csvRow, headerMap, field) {
      if (!Object.prototype.hasOwnProperty.call(headerMap, field)) return "";
      return csvRow.values[headerMap[field]] === undefined
        ? ""
        : String(csvRow.values[headerMap[field]]);
    },
    buildImportRow(csvRow, headerMap, columnCount = null) {
      const row = {
        line: csvRow.line,
        accountType: 0,
        username: this.fieldValue(csvRow, headerMap, "username"),
        domain: this.fieldValue(csvRow, headerMap, "domain").toLowerCase(),
        password: this.fieldValue(csvRow, headerMap, "password"),
        quotaRaw: this.fieldValue(csvRow, headerMap, "quota_gb").trim(),
        quotaGb: 0,
        quotaMb: 0,
        fullName: this.fieldValue(csvRow, headerMap, "full_name"),
        accountSubtypeRaw: this.fieldValue(
          csvRow,
          headerMap,
          "account_subtype"
        ).trim(),
        accountSubtype: 0,
        errors: [],
        status: "pending",
        message: "",
      };

      if (columnCount !== null && csvRow.values.length !== columnCount) {
        this.addRowError(
          row,
          this.$t("USERS.IMPORT.CSV_STRUCTURE"),
          this.$t("USERS.IMPORT.COLUMN_COUNT", {
            expected: columnCount,
            actual: csvRow.values.length,
          })
        );
      } else if (
        columnCount === null &&
        (csvRow.values.length < 3 || csvRow.values.length > 7)
      ) {
        this.addRowError(
          row,
          this.$t("USERS.IMPORT.CSV_STRUCTURE"),
          this.$t("USERS.IMPORT.POSITIONAL_COLUMN_COUNT", {
            actual: csvRow.values.length,
          })
        );
      }

      const typeRaw = this.fieldValue(csvRow, headerMap, "account_type").trim();
      if (
        !/^[0-9]+$/.test(typeRaw) ||
        !ALLOWED_ACCOUNT_TYPES.includes(Number(typeRaw))
      ) {
        this.addRowError(
          row,
          "account_type",
          this.$t("USERS.IMPORT.INVALID_ACCOUNT_TYPE", { value: typeRaw })
        );
      } else {
        row.accountType = Number(typeRaw);
      }

      if (
        row.username.length < 2 ||
        row.username.length > 128 ||
        !USERNAME_REGEX.test(row.username)
      ) {
        this.addRowError(
          row,
          "username",
          this.$t("USERS.IMPORT.INVALID_USERNAME")
        );
      }

      if (!DOMAIN_REGEX.test(row.domain)) {
        this.addRowError(row, "domain", this.$t("USERS.IMPORT.INVALID_DOMAIN"));
      }

      if ([1, 2, 3, 4].includes(row.accountType)) {
        if (!Object.prototype.hasOwnProperty.call(headerMap, "password")) {
          this.addRowError(
            row,
            "password",
            this.$t("USERS.IMPORT.MISSING_TYPE_COLUMN", {
              column: "password",
              type: row.accountType,
            })
          );
        } else {
          this.validatePassword(row);
        }

        if (!Object.prototype.hasOwnProperty.call(headerMap, "quota_gb")) {
          this.addRowError(
            row,
            "quota_gb",
            this.$t("USERS.IMPORT.MISSING_TYPE_COLUMN", {
              column: "quota_gb",
              type: row.accountType,
            })
          );
        } else {
          this.validateQuota(row);
        }
      } else if (row.accountType === 9) {
        if (row.password !== "" || row.quotaRaw !== "" || row.fullName !== "") {
          const field =
            row.password !== ""
              ? "password"
              : row.quotaRaw !== ""
              ? "quota_gb"
              : "full_name";
          this.addRowError(
            row,
            field,
            this.$t("USERS.IMPORT.EXTERNAL_FIELDS_MUST_BE_EMPTY")
          );
        }
      }

      if (row.accountType === 3) {
        if (row.accountSubtypeRaw === "") {
          row.accountSubtype = 0;
        } else if (!["0", "7", "8"].includes(row.accountSubtypeRaw)) {
          this.addRowError(
            row,
            "account_subtype",
            this.$t("USERS.IMPORT.INVALID_SUBTYPE")
          );
        } else {
          row.accountSubtype = Number(row.accountSubtypeRaw);
        }
      } else if (row.accountSubtypeRaw !== "") {
        this.addRowError(
          row,
          "account_subtype",
          this.$t("USERS.IMPORT.SUBTYPE_ONLY_OEX")
        );
      }

      if (!FULL_NAME_REGEX.test(row.fullName)) {
        this.addRowError(
          row,
          "full_name",
          this.$t("USERS.IMPORT.INVALID_FULL_NAME")
        );
      }

      return row;
    },
    validatePassword(row) {
      const strict = JwtService.getComplexPass() === 2;
      const validFormat = strict
        ? COMPLEX_PASSWORD_REGEX.test(row.password)
        : SIMPLE_PASSWORD_REGEX.test(row.password);
      if (
        row.password.length < 3 ||
        row.password.length > 128 ||
        /\s/.test(row.password) ||
        !validFormat
      ) {
        this.addRowError(
          row,
          "password",
          strict
            ? this.$t("USERS.IMPORT.INVALID_COMPLEX_PASSWORD")
            : this.$t("USERS.IMPORT.INVALID_SIMPLE_PASSWORD")
        );
      }
    },
    validateQuota(row) {
      if (!QUOTA_GB_REGEX.test(row.quotaRaw)) {
        this.addRowError(
          row,
          "quota_gb",
          this.$t("USERS.IMPORT.INVALID_QUOTA")
        );
        return;
      }

      row.quotaGb = Number(row.quotaRaw);
      row.quotaMb = Math.round(row.quotaGb * 1000);
      if (row.quotaMb < 1) {
        this.addRowError(
          row,
          "quota_gb",
          this.$t("USERS.IMPORT.INVALID_QUOTA")
        );
        return;
      }

      let maximumGb = null;
      if (row.accountType === 1 || row.accountType === 4) {
        if (row.domain.toLowerCase() === "lexoil.mn") maximumGb = 200;
        else if (row.domain.toLowerCase() !== "remax.mn") maximumGb = 50;
      } else if (row.accountType === 2) maximumGb = 200;
      else if (row.accountType === 3) maximumGb = 100;

      if (maximumGb !== null && row.quotaGb > maximumGb) {
        this.addRowError(
          row,
          "quota_gb",
          this.$t("USERS.IMPORT.QUOTA_TOO_LARGE", { max: maximumGb })
        );
      }
    },
    validateDomainsAndDuplicates() {
      const regular = new Set(
        this.regularDomains.map((item) =>
          String(item.domain || item).toLowerCase()
        )
      );
      const audit = new Set(
        this.auditDomains.map((item) =>
          String(item.domain || item).toLowerCase()
        )
      );
      const addresses = {};

      this.rows.forEach((row) => {
        const domain = row.domain.toLowerCase();
        if (DOMAIN_REGEX.test(row.domain)) {
          if (row.accountType === 4 && !audit.has(domain)) {
            this.addRowError(
              row,
              "domain",
              this.$t("USERS.IMPORT.AUDIT_DOMAIN_REQUIRED")
            );
          } else if (
            [1, 2, 3, 9].includes(row.accountType) &&
            !regular.has(domain)
          ) {
            this.addRowError(
              row,
              "domain",
              this.$t("USERS.IMPORT.REGULAR_DOMAIN_REQUIRED")
            );
          }
        }

        if (
          USERNAME_REGEX.test(row.username) &&
          DOMAIN_REGEX.test(row.domain)
        ) {
          const address = `${row.username}@${row.domain}`.toLowerCase();
          if (!addresses[address]) addresses[address] = [];
          addresses[address].push(row);
        }
      });

      Object.keys(addresses).forEach((address) => {
        if (addresses[address].length > 1) {
          addresses[address].forEach((row) => {
            this.addRowError(
              row,
              "username",
              this.$t("USERS.IMPORT.DUPLICATE_ADDRESS", { address })
            );
          });
        }
      });
    },
    async validateExistingUsers() {
      const candidates = this.rows.filter(
        (row) =>
          row.errors.length === 0 &&
          USERNAME_REGEX.test(row.username) &&
          DOMAIN_REGEX.test(row.domain)
      );
      let nextIndex = 0;
      const worker = async () => {
        while (nextIndex < candidates.length) {
          const row = candidates[nextIndex];
          nextIndex += 1;
          let response;
          try {
            response = await postRequest({
              action: "checkUserExist",
              token: localStorage.id_token,
              username: row.username,
              domain: row.domain,
            });
          } catch (error) {
            response = null;
          }

          if (!response || response.returncode === undefined) {
            this.addRowError(
              row,
              "username",
              this.$t("USERS.IMPORT.USER_CHECK_FAILED")
            );
          } else if (Number(response.returncode) === 1) {
            this.addRowError(
              row,
              "username",
              this.$t("USERS.IMPORT.USER_EXISTS", {
                address: `${row.username}@${row.domain}`,
              })
            );
          }
        }
      };

      const workers = [];
      const workerCount = Math.min(5, candidates.length);
      for (let index = 0; index < workerCount; index += 1)
        workers.push(worker());
      await Promise.all(workers);
    },
    async loadValidationContext() {
      const [statsResponse, timezoneResponse, dateformatResponse] =
        await Promise.all([
          postRequest({
            action: "getAdminStats",
            token: localStorage.id_token,
          }),
          postRequest({
            action: "getAdminTimezone",
            token: localStorage.id_token,
          }),
          postRequest({
            action: "getAdminDateFormat",
            token: localStorage.id_token,
          }),
        ]);

      if (
        !statsResponse ||
        !statsResponse.returncode ||
        !statsResponse.returndata
      ) {
        this.addFileError(
          this.$t("USERS.IMPORT.STATS_FAILED"),
          "",
          this.$t("USERS.IMPORT.RESOURCE")
        );
      } else {
        this.stats = statsResponse.returndata;
      }

      if (typeof timezoneResponse === "string") {
        this.defaults.timezone = timezoneResponse;
      } else if (timezoneResponse && timezoneResponse.returndata) {
        this.defaults.timezone = timezoneResponse.returndata;
      }

      if (dateformatResponse && dateformatResponse.returncode) {
        this.defaults.dateformat =
          dateformatResponse.returndata.dateformat || "";
      }
    },
    validateResources() {
      if (!this.stats) return;
      const checks = [
        [
          "basic",
          "curemails",
          "maxemails",
          this.$t("USERS.IMPORT.BASIC_AUDIT_RESOURCE"),
        ],
        ["enhanced", "cureemails", "maxeemails", this.$t("USERS.ENHANCED")],
        ["oex", "curoexemails", "maxoexemails", this.$t("USERS.OEX")],
        [
          "external",
          "curocemails",
          "maxocemails",
          this.$t("USERS.IMPORT.EXTERNAL"),
        ],
      ];

      checks.forEach(([requestedKey, currentKey, maximumKey, label]) => {
        const requested = this.requestedCounts[requestedKey];
        const available = Math.max(
          0,
          this.numberStat(maximumKey) - this.numberStat(currentKey)
        );
        if (requested > available) {
          this.addFileError(
            this.$t("USERS.IMPORT.INSUFFICIENT_ACCOUNTS", {
              label,
              requested,
              available,
            }),
            "",
            this.$t("USERS.IMPORT.RESOURCE")
          );
        }
      });

      const quotaAvailable = Math.max(
        0,
        this.numberStat("maxquota") - this.numberStat("curquota")
      );
      if (
        String(this.stats.autogrow_quota) !== "1" &&
        this.requestedCounts.quota > quotaAvailable
      ) {
        this.addFileError(
          this.$t("USERS.IMPORT.INSUFFICIENT_QUOTA", {
            requested: this.formatGb(this.requestedCounts.quota),
            available: this.formatGb(quotaAvailable),
          }),
          "",
          this.$t("USERS.IMPORT.RESOURCE")
        );
      }
    },
    async validateImport() {
      if (!this.importFile || this.stage === "validating") return;
      this.stage = "validating";
      this.rows = [];
      this.fileErrors = [];
      this.stats = null;

      if (!/\.csv$/i.test(this.importFile.name || "")) {
        this.addFileError(this.$t("USERS.IMPORT.NOT_CSV"));
        this.stage = "errors";
        return;
      }
      if (this.importFile.size > MAX_FILE_SIZE) {
        this.addFileError(this.$t("USERS.IMPORT.FILE_TOO_LARGE"));
        this.stage = "errors";
        return;
      }

      let contents;
      try {
        contents = await this.readFile(this.importFile);
      } catch (error) {
        this.addFileError(this.$t("USERS.IMPORT.FILE_READ_FAILED"));
        this.stage = "errors";
        return;
      }

      try {
        const parsed = parseCsv(contents);
        parsed.errors.forEach((error) => {
          this.addFileError(
            this.$t(
              CSV_ERROR_TRANSLATIONS[error.code] || "USERS.IMPORT.CSV_INVALID"
            ),
            error.line,
            this.$t("USERS.IMPORT.CSV_STRUCTURE")
          );
        });

        const csvRows = parsed.rows.filter((row) => !isBlankCsvRow(row));
        if (csvRows.length === 0) {
          this.addFileError(this.$t("USERS.IMPORT.NO_ROWS"));
          this.stage = "errors";
          return;
        }

        const hasHeader = this.hasHeaderRow(csvRows[0]);
        const dataRows = hasHeader ? csvRows.slice(1) : csvRows;
        if (dataRows.length === 0) {
          this.addFileError(this.$t("USERS.IMPORT.NO_ROWS"));
          this.stage = "errors";
          return;
        }
        if (dataRows.length > MAX_IMPORT_ROWS) {
          this.addFileError(
            this.$t("USERS.IMPORT.TOO_MANY_ROWS", { max: MAX_IMPORT_ROWS })
          );
          this.stage = "errors";
          return;
        }

        const headerMap = hasHeader
          ? this.buildHeaderMap(csvRows[0])
          : POSITIONAL_HEADER_MAP;
        const columnCount = hasHeader ? csvRows[0].values.length : null;
        this.rows = dataRows.map((row) =>
          this.buildImportRow(row, headerMap, columnCount)
        );
        this.validateDomainsAndDuplicates();

        await this.loadValidationContext();
        await this.validateExistingUsers();
        this.validateResources();

        this.stage = this.validationErrors.length > 0 ? "errors" : "ready";
      } catch (error) {
        this.addFileError(this.$t("USERS.IMPORT.VALIDATION_REQUEST_FAILED"));
        this.stage = "errors";
      }
    },
    numberStat(field) {
      const value = Number(this.stats && this.stats[field]);
      return Number.isFinite(value) ? value : 0;
    },
    formatGb(mb) {
      return `${Math.round((Number(mb) / 1000) * 1000) / 1000} GB`;
    },
    quotaDisplay(row) {
      return row.accountType === 9 ? "—" : `${row.quotaRaw} GB`;
    },
    accountTypeLabel(type) {
      const labels = {
        1: this.$t("USERS.BASIC"),
        2: this.$t("USERS.ENHANCED"),
        3: this.$t("USERS.OEX"),
        4: this.$t("USERS.AUDIT_ACCOUNT"),
        9: this.$t("USERS.IMPORT.EXTERNAL"),
      };
      return labels[type] || String(type || "");
    },
    standardLanguage() {
      const language = String(localStorage.language || "en").toLowerCase();
      return language === "ptbr" ? "pt-br" : language;
    },
    oexLanguage() {
      const languages = {
        de: "de_DE",
        es: "es_ES",
        en: "en_US",
        fr: "fr_FR",
        id: "en_US",
        ptbr: "pt_BR",
        "pt-br": "pt_BR",
        "pt-pt": "pt_PT",
      };
      return (
        languages[String(localStorage.language || "en").toLowerCase()] ||
        "en_US"
      );
    },
    requestForRow(row) {
      const request = {
        action: "addUser",
        token: localStorage.id_token,
        account_type: row.accountType,
        username: row.username,
        domain: row.domain,
      };

      if (row.accountType === 9) return request;

      request.password = row.password;
      request.quota = row.quotaRaw;
      request.uname = row.fullName;
      request.user_language =
        row.accountType === 3 ? this.oexLanguage() : this.standardLanguage();

      if (row.accountType === 3) {
        request.account_subtype = row.accountSubtype;
      } else {
        request.twofa_allowed =
          String(localStorage.isTfaAllowed) === "1" ? 1 : 0;
        request.timezone = this.defaults.timezone;
        request.dateformat = this.defaults.dateformat;
      }

      return request;
    },
    responseMessage(response, fallback) {
      if (!response || response.returndata === undefined) return fallback;
      if (
        typeof response.returndata === "string" ||
        typeof response.returndata === "number"
      ) {
        return String(response.returndata);
      }
      try {
        return JSON.stringify(response.returndata);
      } catch (error) {
        return fallback;
      }
    },
    async waitForNextRow() {
      this.nextRowDelay = IMPORT_DELAY_SECONDS;
      await new Promise((resolve) => {
        this.clearDelayTimer();
        this.delayTimer = setInterval(() => {
          this.nextRowDelay -= 1;
          if (this.nextRowDelay <= 0) {
            this.clearDelayTimer();
            resolve();
          }
        }, 1000);
      });
    },
    async processImport() {
      if (this.stage !== "ready") return;
      this.stage = "importing";
      this.importFile = null;
      this.processedCount = 0;
      this.rows.forEach((row) => {
        row.status = "pending";
        row.message = "";
      });

      for (let index = 0; index < this.rows.length; index += 1) {
        const row = this.rows[index];
        this.$set(row, "status", "processing");
        this.$set(row, "message", this.$t("USERS.IMPORT.PROCESSING"));
        let response;
        try {
          response = await postRequest(this.requestForRow(row));
        } catch (error) {
          response = null;
        }

        const payload =
          response && response.returncode === undefined && response.data
            ? response.data
            : response;

        if (payload && Number(payload.returncode) === 1) {
          this.$set(row, "status", "success");
          this.$set(
            row,
            "message",
            this.responseMessage(payload, this.$t("COMMON.SUCCESS"))
          );
        } else {
          this.$set(row, "status", "failed");
          this.$set(
            row,
            "message",
            this.responseMessage(
              payload,
              this.$t("USERS.IMPORT.UNKNOWN_IMPORT_ERROR")
            )
          );
        }

        row.password = "";
        this.processedCount = index + 1;
        if (index < this.rows.length - 1) await this.waitForNextRow();
      }

      this.nextRowDelay = 0;
      this.stage = "complete";
      this.$emit("completed", {
        total: this.rows.length,
        successful: this.successfulRows.length,
        failed: this.failedRows.length,
      });
    },
    statusVariant(status) {
      return (
        {
          pending: "secondary",
          processing: "primary",
          success: "success",
          failed: "danger",
        }[status] || "secondary"
      );
    },
    statusLabel(status) {
      const labels = {
        pending: this.$t("USERS.IMPORT.STATUS_PENDING"),
        processing: this.$t("USERS.IMPORT.STATUS_PROCESSING"),
        success: this.$t("USERS.IMPORT.STATUS_SUCCESS"),
        failed: this.$t("USERS.IMPORT.STATUS_FAILED"),
      };
      return labels[status] || status;
    },
    downloadTemplate() {
      const csv = `${this.selectedFormatExample.replace("\n", "\r\n")}\r\n`;
      const url = URL.createObjectURL(
        new Blob([csv], { type: "text/csv;charset=utf-8" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `user-import-type-${this.formatAccountType}-template.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.import-steps {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.import-step {
  border-bottom: 3px solid #d8d8d8;
  color: #777;
  padding: 8px 4px;
  text-align: center;
}

.import-step.active {
  border-color: #3699ff;
  color: #1e1e2d;
  font-weight: 600;
}

.import-step.complete {
  border-color: #1bc5bd;
  color: #1bc5bd;
}

.import-table-scroll {
  max-height: 420px;
  overflow-y: auto;
}

.import-format {
  background: #f3f6f9;
  border-radius: 4px;
  overflow-x: auto;
  padding: 12px;
}

.format-column-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.format-column-list code {
  background: #f3f6f9;
  border-radius: 4px;
  padding: 5px 8px;
}

@media (max-width: 767px) {
  .import-steps {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
